import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaPlus, FaSearch} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import individualMonitoringWatchlistService, {
    INDIVIDUAL_MONITORING_WATCHLIST_QUERY_KEYS
} from "../../../../services/web/individual-monitoring-watchlist-service.js";
import Alert from "../../../../components/alert/alert.jsx";

const IndividualMonitoringWatchlist = () => {
    const navigate = navigateHook();

    const [data, setData] = useState([])
    const [filter, setFilter] = useState("");

    const {data: dataFetch, isSuccess} = useQuery({
        queryFn: () => individualMonitoringWatchlistService.list(),
        queryKey: INDIVIDUAL_MONITORING_WATCHLIST_QUERY_KEYS.list,
    });

    const {data: status, isError} = useQuery({
        queryFn: () => individualMonitoringWatchlistService.status(),
        queryKey: INDIVIDUAL_MONITORING_WATCHLIST_QUERY_KEYS.status,
        retry: false
    });

    useEffect(() => {
        if (!isSuccess) return;

        setData(dataFetch);
    }, [isSuccess, dataFetch]);

    const [columnDefs,] = useState([
        { field: "name", headerName: "Nome", sort: "asc", flex: 1, comparator: aggridUtils.nullLastComparator},
    ]);

    const gridRef = useRef(undefined);

    const defaultColDef = useMemo(() => ({resizable: false}), []);

    const apply = useCallback(() => {
        setData(dataFetch?.filter(d => d.name?.toLowerCase().includes(filter?.toLowerCase())))
    }, [filter, dataFetch])

    const clear = useCallback(() => {
        setData(dataFetch);
        setFilter("");
    }, [dataFetch])

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Enter") {
                apply();
            }
            if (event.key === "Escape") {
                clear();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [apply, clear]);

    const onCellClicked = ({data}) => {
        navigate(`/individual-monitoring-watchlist/form/${data.id}`)
    };

    return (
        <Template>
            <Template.Header>
                <Template.Title>Lista de monitoramento</Template.Title>

                <Button target={"primary"} onClick={() => navigate("/individual-monitoring-watchlist/form")}>
                    <FaPlus/> Adicionar item na lista de monitoramento
                </Button>
            </Template.Header>

            <Template.Body className={"space-y-2"}>
                <Card>
                    <Card.Body>
                        <div className="flex justify-between gap-4">
                            <InputForm
                                classNameWrapper={"w-full"}
                                id={"name"}
                                label={"Nome"}
                                name={"name"}
                                value={filter}
                                onChange={({target: {value}}) => setFilter(value)}
                            />

                            <div className={"flex gap-2"}>
                                <Button target={"default"} onClick={clear} title={"Limpar"}>
                                    <FaEraser />
                                </Button>
                                <Button onClick={apply} title={"Buscar"}>
                                    <FaSearch />
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body className={"space-y-2"}>
                        {
                            isError || !status &&
                            <Alert target={"danger"}>
                                Sistema de reconhecimento facial não configurado.
                            </Alert>
                        }

                        <div className={"aggrid-row-pointer"} style={{height: (isError || !status) ? "calc(100vh - 201px)" : "calc(100vh - 153px)"}}>
                            <AgGridReact
                                ref={gridRef}
                                rowData={data ?? []}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                pagination
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                onCellClicked={onCellClicked}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default IndividualMonitoringWatchlist;
